export type SlotSource = 'api' | 'custom';

export type WeekdayKey = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';

export interface DayAvailability {
  enabled: boolean;
  start: string;
  end: string;
}

export interface SlotApiConfig {
  url: string;
  /** Dot path to the slot list in the JSON response, e.g. "schedule" or "data.slots". */
  path: string;
  /** Sends the visible month as filter[startAt] / filter[endAt] query params. */
  sendRange: boolean;
}

export interface CustomSlotConfig {
  timezone: string;
  durationMinutes: number;
  bufferMinutes: number;
  minNoticeHours: number;
  maxDaysAhead: number;
  weeklyHours: Record<WeekdayKey, DayAvailability>;
  blockedDates: string[];
}

export interface SlotBookingConfig {
  source: SlotSource;
  api: SlotApiConfig;
  custom: CustomSlotConfig;
  showTimezone: boolean;
  use24Hour: boolean;
}

export interface SlotItem {
  iso: string;
  endIso?: string;
}

export interface DisplaySlot extends SlotItem {
  label: string;
}

export const WEEKDAYS: { key: WeekdayKey; label: string }[] = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

/** Index matches Date#getDay(). */
export const WEEKDAY_BY_INDEX: WeekdayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const workday = (): DayAvailability => ({ enabled: true, start: '09:00', end: '17:00' });
const dayOff = (): DayAvailability => ({ enabled: false, start: '09:00', end: '17:00' });

export const createDefaultSlotConfig = (): SlotBookingConfig => ({
  source: 'custom',
  api: {
    url: '',
    path: 'schedule',
    sendRange: true,
  },
  custom: {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    durationMinutes: 30,
    bufferMinutes: 0,
    minNoticeHours: 2,
    maxDaysAhead: 60,
    weeklyHours: {
      mon: workday(),
      tue: workday(),
      wed: workday(),
      thu: workday(),
      fri: workday(),
      sat: dayOff(),
      sun: dayOff(),
    },
    blockedDates: [],
  },
  showTimezone: true,
  use24Hour: false,
});

/** Fills any keys missing from a saved config (older fields, partial edits) with defaults. */
export const resolveSlotConfig = (raw?: Partial<SlotBookingConfig> | null): SlotBookingConfig => {
  const defaults = createDefaultSlotConfig();
  if (!raw) return defaults;
  return {
    ...defaults,
    ...raw,
    api: { ...defaults.api, ...(raw.api || {}) },
    custom: {
      ...defaults.custom,
      ...(raw.custom || {}),
      weeklyHours: {
        ...defaults.custom.weeklyHours,
        ...(raw.custom?.weeklyHours || {}),
      },
      blockedDates: raw.custom?.blockedDates || [],
    },
  };
};
